import React, { useEffect, useState } from "react";
import cx from "classnames";
import Explosion from "../../../assets/img/explosion.gif";
import Robot from "../../../assets/img/robot-arms-up-35X35.png";
import SelectionActions from "../../actions/SelectionActions";
import UIActions from "../../actions/UIActions";
import useWindowPortal from "../../hooks/useWindowPortal";
import SiteDesignStore from "../../stores/SiteDesignStore";
import { copyToClipboard } from "../../utils/AppUtils";
import When from "../utils/When";
import DispatchMonitoring from "./DispatchMonitoring";
import GeneralTabContent from "./GeneralTabContent";
import RefluxMonitoring from "./RefluxMonitoring";
import TabButton from "./TabButton";
import styles from "./DevTools.module.css";

const DEVTOOLS_WINDOW_KEY = "siDevToolsWindow";

/**
 * Renders the DevTools content (tabs + content area).
 * Used for both inline panel and pop-out window.
 * @param {Object} props
 * @param {boolean} [props.inPopout] - Whether rendering in pop-out window
 * @param {string} props.activeTab - Currently active tab ('general' | 'reflux' | 'dispatch')
 * @param {Function} props.setActiveTab - Tab change handler
 * @param {boolean} props.expanded - Whether panel is expanded
 * @param {Function} props.setExpanded - Expand/collapse handler
 * @param {Function} props.onHidePanel - Handler to hide panel
 * @param {Function} props.onDebugJSON - Handler to open JSON editor
 * @param {Function} props.onCopyPageID - Handler to copy page ID
 * @param {Function} props.onToggleStageSelectLayer - Handler to toggle stage select layer
 * @param {Function} props.onPopOut - Handler to pop out to new window
 * @param {Function} props.onPopIn - Handler to pop back into main window
 */
const DevToolsContent = ({
  inPopout = false,
  activeTab,
  setActiveTab,
  expanded,
  setExpanded,
  onHidePanel,
  onDebugJSON,
  onCopyPageID,
  onToggleStageSelectLayer,
  onPopOut,
  onPopIn,
}) => (
  <div className={cx(styles.container, { [styles.containerPopout]: inPopout })}>
    {/* Tab Bar */}
    <div className={styles.tabBar}>
      {/* Left tabs - main navigation */}
      <div className={styles.tabGroup}>
        <TabButton
          active={activeTab === "general"}
          onClick={() => setActiveTab("general")}
        >
          General
        </TabButton>
        <TabButton
          active={activeTab === "reflux"}
          onClick={() => setActiveTab("reflux")}
        >
          Reflux
        </TabButton>
        <TabButton
          active={activeTab === "dispatch"}
          onClick={() => setActiveTab("dispatch")}
        >
          Dispatch
        </TabButton>
      </div>

      {/* Right tabs - window controls */}
      <div className={styles.tabGroup}>
        {inPopout ? (
          <TabButton
            icon
            noBorder
            onClick={onPopIn}
            title="Pop back into main window"
          >
            ⧉
          </TabButton>
        ) : (
          <>
            <TabButton
              icon
              onClick={onPopOut}
              title="Pop out to new window"
            >
              ⧉
            </TabButton>
            <TabButton
              icon
              noBorder
              onClick={() => setExpanded(!expanded)}
              title={expanded ? "Collapse panel" : "Expand panel"}
            >
              {expanded ? "▼" : "▲"}
            </TabButton>
          </>
        )}
      </div>
    </div>

    {/* Tab Content */}
    <div
      className={cx(styles.tabContent, {
        [styles.tabContentGeneral]: activeTab === "general",
      })}
    >
      {activeTab === "general" && (
        <GeneralTabContent
          onHidePanel={onHidePanel}
          onDebugJSON={onDebugJSON}
          onCopyPageID={onCopyPageID}
          onToggleStageSelectLayer={onToggleStageSelectLayer}
        />
      )}
      {activeTab === "reflux" && <RefluxMonitoring />}
      {activeTab === "dispatch" && <DispatchMonitoring />}
    </div>
  </div>
);

/**
 * Main DevTools component.
 * Provides development utilities panel accessible via robot icon.
 * Supports both inline panel and pop-out window modes.
 */
const DevTools = () => {
  const [showToolPanel, setShowToolPanel] = useState(false);
  const [explosion, setExplosion] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [expanded, setExpanded] = useState(false);
  const [isPoppedOut, setIsPoppedOut] = useState(() => {
    try {
      const state = localStorage.getItem(DEVTOOLS_WINDOW_KEY);
      return state ? JSON.parse(state).windowName != null : false;
    } catch {
      return false;
    }
  });

  const { PortalWindow, openWindow, closeWindow, focusWindow } = useWindowPortal({
    title: "Showit DevTools",
    storageKey: DEVTOOLS_WINDOW_KEY,
    bodyClassName: "si-devtools-popout-body",
    defaultWidth: 900,
    defaultHeight: 600,
    onClose: () => setIsPoppedOut(false),
  });

  // Auto-open popup if it was previously open before refresh
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount - isPoppedOut is initial value from localStorage
  useEffect(() => {
    if (isPoppedOut) {
      openWindow();
    }
  }, []);

  const _onShowPanel = () => {
    // If popped out, focus the pop-out window instead of toggling inline panel
    if (isPoppedOut) {
      focusWindow();
      return;
    }

    if (!showToolPanel) {
      setExplosion(true);
      setTimeout(() => {
        setExplosion(false);
      }, 600);
    }
    setShowToolPanel(!showToolPanel);
  };

  const _onHidePanel = () => {
    setTimeout(() => setShowToolPanel(!showToolPanel), 1000);
  };

  const _onDebugJSON = () => {
    setShowToolPanel(!showToolPanel);
    UIActions.toggleJSONEditor();
  };

  const _onCopyPageID = () => {
    const currentPageID = SiteDesignStore.getCurrentPage().id;
    copyToClipboard({ val: currentPageID || "", showToast: true });
  };

  const _onToggleStageSelectLayer = () => {
    SelectionActions.toggleStageSelectLayer();
  };

  const _onPopOut = () => {
    const success = openWindow();
    if (success) {
      setIsPoppedOut(true);
      setShowToolPanel(false); // Close inline panel when popping out
    }
  };

  const _onPopIn = () => {
    closeWindow();
    setIsPoppedOut(false);
  };

  const contentProps = {
    activeTab,
    setActiveTab,
    expanded,
    setExpanded,
    onHidePanel: _onHidePanel,
    onDebugJSON: _onDebugJSON,
    onCopyPageID: _onCopyPageID,
    onToggleStageSelectLayer: _onToggleStageSelectLayer,
    onPopOut: _onPopOut,
    onPopIn: _onPopIn,
  };

  return (
    <aside>
      {/* Inline panel */}
      <div
        className={`si-devtools-panel${showToolPanel ? " open" : ""}${expanded ? " expanded" : ""}`}
      >
        <div
          style={{
            display: showToolPanel ? "flex" : "none",
            width: "100%",
            height: "100%",
          }}
        >
          <DevToolsContent {...contentProps} />
        </div>
      </div>

      {/* Pop-out window portal */}
      {isPoppedOut && (
        <PortalWindow>
          <DevToolsContent {...contentProps} inPopout />
        </PortalWindow>
      )}

      <button
        type="button"
        onClick={_onShowPanel}
        title="Showit DevTools"
        className={`si-robot${showToolPanel ? " open" : ""}${isPoppedOut ? " popped-out" : ""}`}
        style={{
          background: "none",
          border: "none",
        }}
      >
        <img src={Robot} alt="Showit Robot" style={{ display: "block" }} />
      </button>
      <When it={explosion}>
        <img className="si-explosion" src={Explosion} alt="Explosion" />
      </When>
    </aside>
  );
};

export default DevTools;
